import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Plus, Search, Pencil, Trash2, Droplets, ClipboardCheck } from "lucide-react";
import { PrinterForm, emptyPrinterForm, printerToForm, buildPrinterPayload } from "../printers/PrinterForm";
import { CheckDialog } from "../printers/CheckDialog";
import { PRINTER_TYPES, PRINTER_STATUS, PrinterStatusBadge, WarrantyBadge, CheckBadge, labelOf, Pill } from "../printers/constants";

export default function PrintersListView({ sites, companies, onChanged, refreshKey, searchParams }) {
    const navigate = useNavigate();
    const { hasPermission } = useApp();
    const [printers, setPrinters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [siteFilter, setSiteFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
    const [typeFilter, setTypeFilter] = useState("");
    const [overdueOnly, setOverdueOnly] = useState(searchParams.get("overdue") === "1");
    const [dialog, setDialog] = useState(null);
    const [form, setForm] = useState(emptyPrinterForm());
    const [checkPrinter, setCheckPrinter] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const params = {};
            if (search.trim()) params.search = search.trim();
            if (siteFilter) params.site_id = siteFilter;
            if (statusFilter) params.status = statusFilter;
            if (typeFilter) params.type = typeFilter;
            const r = await api.getPrinters(params);
            setPrinters(r.printers);
        } catch { toast.error("Erreur de chargement"); }
        finally { setLoading(false); }
    }, [search, siteFilter, statusFilter, typeFilter]);

    useEffect(() => { load(); }, [load, refreshKey]);

    const defaultSite = sites.find((s) => s.is_default)?.id || "";
    const visible = overdueOnly
        ? printers.filter((p) => p.status === "active" && (!p.last_check_at || (new Date() - new Date(p.last_check_at)) / 86400000 > 90))
        : printers;

    const openCreate = () => { setForm(emptyPrinterForm(defaultSite)); setDialog({ mode: "create" }); };
    const openEdit = (p) => { setForm(printerToForm(p)); setDialog({ mode: "edit", printer: p }); };

    const save = async () => {
        if (!form.name.trim()) { toast.error("Nom requis"); return; }
        setSaving(true);
        try {
            if (dialog.mode === "edit") { await api.updatePrinter(dialog.printer.id, buildPrinterPayload(form)); toast.success("Imprimante mise à jour"); }
            else { const p = await api.createPrinter(buildPrinterPayload(form)); toast.success(`"${p.name}" ajoutée`); }
            setDialog(null);
            load();
            onChanged();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        finally { setSaving(false); }
    };

    const remove = async (p) => {
        if (!window.confirm(`Supprimer l'imprimante "${p.name}" ?`)) return;
        try { await api.deletePrinter(p.id); toast.success("Imprimante supprimée"); load(); onChanged(); }
        catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    return (
        <div data-testid="printers-list-view">
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-sm">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nom, modèle, IP, N° série, emplacement..." className="pl-9" data-testid="printer-search" />
                </div>
                <select value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)} className="px-3 py-2 text-sm border rounded-md bg-white" data-testid="printer-site-filter">
                    <option value="">Tous les sites</option>
                    {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 text-sm border rounded-md bg-white" data-testid="printer-status-filter">
                    <option value="">Tous statuts</option>
                    {Object.entries(PRINTER_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-3 py-2 text-sm border rounded-md bg-white" data-testid="printer-type-filter">
                    <option value="">Tous types</option>
                    {PRINTER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} data-testid="printer-overdue-filter" />Contrôle en retard
                </label>
                {hasPermission("printers:manage") && <Button onClick={openCreate} className="ml-auto" data-testid="create-printer-btn"><Plus className="w-4 h-4 mr-1" />Nouvelle imprimante</Button>}
            </div>

            <div className="card">
                <div className="card-body p-0 overflow-x-auto">
                    {loading ? <p className="text-sm text-gray-400 py-8 text-center animate-pulse">Chargement...</p>
                        : visible.length === 0 ? <p className="text-sm text-gray-400 py-8 text-center">Aucune imprimante</p> : (
                            <table className="w-full text-sm">
                                <thead><tr className="border-b border-gray-100 text-left">
                                    {["Imprimante", "Type", "IP", "Site / Emplacement", "Statut", "Consommables", "Dernier contrôle", "Garantie", ""].map((h) => (
                                        <th key={h} className={`px-4 py-2.5 text-xs font-medium text-gray-500 ${h === "" ? "text-right" : ""}`}>{h}</th>
                                    ))}
                                </tr></thead>
                                <tbody>
                                    {visible.map((p) => (
                                        <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/admin/inventory/printers/${p.id}`)} data-testid={`printer-row-${p.id}`}>
                                            <td className="px-4 py-2.5">
                                                <div className="font-medium text-gray-900">{p.name}</div>
                                                <div className="text-[11px] text-gray-500">{[p.brand, p.model].filter(Boolean).join(" ")}{p.serial_number ? <span className="font-mono"> · SN {p.serial_number}</span> : ""}</div>
                                            </td>
                                            <td className="px-4 py-2.5 text-xs text-gray-600">{labelOf(PRINTER_TYPES, p.type)}</td>
                                            <td className="px-4 py-2.5 text-xs font-mono text-gray-700">{p.ip_address || "—"}{p.hostname && <div className="text-[10px] text-gray-400">{p.hostname}</div>}</td>
                                            <td className="px-4 py-2.5 text-xs">
                                                <span className="font-semibold text-teal-700">{p.site_name || "—"}</span>
                                                {p.location && <div className="text-[11px] text-gray-500">{p.location}</div>}
                                            </td>
                                            <td className="px-4 py-2.5"><PrinterStatusBadge status={p.status} testId={`printer-status-${p.id}`} /></td>
                                            <td className="px-4 py-2.5 text-xs">
                                                <span className="inline-flex items-center gap-1 text-gray-600"><Droplets className="w-3 h-3" />{p.consumables_count}</span>
                                                {p.low_stock_count > 0 && <Pill className="bg-orange-100 text-orange-700 ml-1.5" testId={`printer-low-${p.id}`}>{p.low_stock_count} stock bas</Pill>}
                                            </td>
                                            <td className="px-4 py-2.5"><CheckBadge printer={p} /></td>
                                            <td className="px-4 py-2.5"><WarrantyBadge end={p.warranty_end} /></td>
                                            <td className="px-4 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                <Button variant="ghost" size="sm" onClick={() => setCheckPrinter(p)} title="Nouveau contrôle" data-testid={`printer-check-${p.id}`}><ClipboardCheck className="w-3.5 h-3.5" /></Button>
                                                <Button variant="ghost" size="sm" onClick={() => openEdit(p)} title="Modifier" data-testid={`printer-edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                                                {hasPermission("admin") && <Button variant="ghost" size="sm" onClick={() => remove(p)} title="Supprimer" data-testid={`printer-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                </div>
            </div>

            <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>{dialog?.mode === "edit" ? `Modifier — ${dialog.printer.name}` : "Nouvelle imprimante"}</DialogTitle></DialogHeader>
                    <div className="py-2 max-h-[65vh] overflow-y-auto pr-1">
                        <PrinterForm form={form} setForm={setForm} sites={sites} companies={companies} />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialog(null)}>Annuler</Button>
                        <Button onClick={save} disabled={saving} data-testid="printer-save-btn">{saving ? "..." : dialog?.mode === "edit" ? "Enregistrer" : "Créer"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <CheckDialog printer={checkPrinter} open={!!checkPrinter} onClose={() => setCheckPrinter(null)} onDone={() => { load(); onChanged(); }} />
        </div>
    );
}
